import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Briefcase, CheckCircle2, FileText } from "lucide-react";

type JourneyStage = "resume" | "job";

interface OnboardingJourneyProps {
  activeStage: JourneyStage;
}

const stages = [
  {
    id: "resume",
    title: "Build Your Story",
    description: "Start by adding your resume so HiredCoach can understand your background and tailor coaching to you.",
    icon: FileText,
  },
  {
    id: "job",
    title: "Aim at the Right Role",
    description: "Add a job listing next so your prep, interview practice, and analysis are pointed at a real opportunity.",
    icon: Briefcase,
  },
  {
    id: "practice",
    title: "Show Up Ready",
    description: "Practice interviews, refine your pitch, and prepare for negotiations with focused coaching.",
    icon: CheckCircle2,
  },
] as const;

export function OnboardingJourney({ activeStage }: OnboardingJourneyProps) {
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {stages.map((stage, index) => {
        const isActive = stage.id === activeStage;
        const Icon = stage.icon;

        return (
          <Card
            key={stage.id}
            className={`rounded-3xl border-2 transition-all ${
              isActive
                ? "border-sky-500 bg-sky-50 shadow-xl shadow-sky-100"
                : "border-slate-200 bg-white shadow-sm"
            }`}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${isActive ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-700"}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <span className="text-sm font-semibold text-slate-400">Step {index + 1}</span>
              </div>

              <h3 className="mt-5 text-2xl font-bold text-slate-900">{stage.title}</h3>
              <p className="mt-3 text-sm leading-6 text-slate-600">{stage.description}</p>

              <div className="mt-5">
                {isActive ? (
                  <Badge className="bg-sky-600 text-white hover:bg-sky-600">You are here</Badge>
                ) : (
                  <Badge variant="secondary" className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                    Coming next
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default OnboardingJourney;
